<template>
  <module-template
    id="scrolltainer"
    title
    :canEdit="$store.state.retentionPolicyPermission && !policy?.immutable"
    canList
    :canDelete="$store.state.retentionPolicyPermission && !policy?.immutable"
    :deleteConfirm="false"
    @deleteItem="showConfirm = true"
    @editItem="editPolicy"
    @list="listPolicies"
  >
    <template #title>
      <v-row dense class="px-1" align="center">
        {{ $t("global.retention-policy.label") }}
        <v-icon color="white" class="px-3" size="9">pic-arrow-right</v-icon>
        <div>{{ policy.name }}</div>
      </v-row>
    </template>

    <basic-card-horizontal :headers="headers" :item="item" />

    <v-card v-if="policy.immutable" class="mt-4 mb-4">
      <v-card-text>
        <v-alert type="info" outlined>
          {{ $t("global.retention-policy.immutable-warning") }}
        </v-alert>
      </v-card-text>
    </v-card>

    <v-card v-if="fileEnvs.length > 0" class="mt-4 mb-4">
      <v-card-title>
        {{ $t("global.retention-policy.file-envs-using") }} ({{
          fileEnvs.length
        }})
      </v-card-title>
      <v-card-text>
        <file-env-chip
          v-for="env in fileEnvs"
          :key="env.id"
          :color="env.color"
          :label="env.label"
          class="mr-2 mb-2"
        />
      </v-card-text>
    </v-card>

    <v-dialog v-model="showConfirm" persistent :width="500">
      <v-overlay absolute :value="deleting">
        <v-chip color="blue" dark>{{ $t("global.msg.deleting") }}</v-chip>
      </v-overlay>

      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("global.action.confirm") }}</span>
        </v-card-title>
        <v-card-text>
          <span>{{ $t("global.retention-policy.delete-warning") }}</span>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn
            depressed
            data-cy-id="confirm-delete-button"
            color="error"
            class="is-action"
            @click="deletePolicy()"
          >
            {{ $t("global.action.yes") }}
          </v-btn>
          <v-btn depressed class="button" @click="showConfirm = false">
            {{ $t("global.action.no") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </module-template>
</template>

<script>
import { get, remove } from "@/model/api";
import ModuleTemplate from "@/components/layout/ModuleTemplate";
import BasicCardHorizontal from "@/components/BasicCardHorizontal";
import FileEnvChip from "@/components/FileEnvChip";

export default {
  name: "RetentionPolicy",
  components: {
    ModuleTemplate,
    BasicCardHorizontal,
    FileEnvChip,
  },

  computed: {
    item() {
      return {
        standaloneRuns: this.formatCategory(this.policy.standaloneRuns),
        bpInstanceRuns: this.formatCategory(this.policy.bpInstanceRuns),
        bpInstances: this.formatCategory(this.policy.bpInstances),
        unusedFiles: this.formatCategory(this.policy.unusedFiles),
      };
    },
  },

  data() {
    return {
      policy: {},
      fileEnvs: [],
      showConfirm: false,
      deleting: false,
      headers: [
        {
          text: this.$t("global.retention-policy.standalone-runs"),
          value: "standaloneRuns",
        },
        {
          text: this.$t("global.retention-policy.bp-instance-runs"),
          value: "bpInstanceRuns",
        },
        {
          text: this.$t("global.retention-policy.bp-instances"),
          value: "bpInstances",
        },
        {
          text: this.$t("global.retention-policy.unused-files"),
          value: "unusedFiles",
        },
      ],
    };
  },

  methods: {
    listPolicies() {
      this.$router.push({ name: "Retention policies" });
    },

    async getPolicy(id) {
      return await get("/retention-policy/" + id).catch((error) => {
        this.$store.commit("showError", error);
      });
    },

    async getFileEnvsForPolicy(policyId) {
      return await get("/retention-policy/" + policyId + "/file-envs").catch(
        (error) => {
          this.$store.commit("showError", error);
        }
      );
    },

    async deletePolicy() {
      this.deleting = true;
      await remove("/retention-policy/" + this.policy.id)
        .then(() => {
          this.$router.push({ name: "Retention policies" });
          this.deleting = false;
          this.showConfirm = false;
        })
        .catch((e) => {
          this.$store.commit(
            "showError",
            this.$t("global." + e.message, e.arguments)
          );
          this.deleting = false;
          this.showConfirm = false;
        });
    },

    editPolicy() {
      this.$router.push({
        name: "Edit retention policy",
        params: { policyId: this.policy.id },
      });
    },

    formatCategory(category) {
      if (!category) return "-";
      if (category.noDeletion) {
        return this.$t("global.retention-policy.no-deletion");
      }
      if (category.value && category.unit) {
        const unitText = this.$t(
          "global.retention-policy.unit." + category.unit.toLowerCase()
        );
        return this.$t("global.retention-policy.delete-after", [
          category.value,
          unitText,
        ]);
      }
      return "-";
    },
  },

  async created() {
    const policyId = this.$route.params.policyId;
    this.policy = await this.getPolicy(policyId);

    const envs = await this.getFileEnvsForPolicy(policyId);
    if (envs) {
      this.fileEnvs = envs;
    }
  },
};
</script>
